/**
 * FeedbackSheet — modal sheet for general (non-message-specific) feedback.
 *
 * Triggered by the "Send feedback" link in the disclaimer footer.  Submits
 * a rating="general" row to /feedback with the typed comment, no message
 * context.  Use this for layout bugs, voice issues, UX requests, etc.
 *
 * Uses the same sheet visual style as PromptsSheet for consistency.
 */
import { useState, useEffect } from "react";
import { submitFeedback } from "../lib/feedback";

interface FeedbackSheetProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
}

export default function FeedbackSheet({ open, onClose, sessionId }: FeedbackSheetProps) {
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset when closed
      const t = setTimeout(() => {
        setComment("");
        setSending(false);
        setDone(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = async () => {
    if (sending || !comment.trim()) return;
    setSending(true);
    try {
      await submitFeedback({
        session_id: sessionId,
        rating: "general",
        comment: comment.trim(),
      });
      setDone(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      console.warn("feedback submit failed:", err);
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      // Top-anchored on mobile, centered on desktop.  Keyboard appears at the
      // bottom of mobile viewports when the textarea autoFocuses; bottom-
      // anchoring the sheet would get it eaten by the keyboard.
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-scout-bg border-b sm:border border-white/10 sm:rounded-2xl shadow-2xl p-5 sm:p-6 pt-safe sm:pt-6 animate-slide-up"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-white">Send feedback</h2>
            <p className="text-xs text-white/40 mt-0.5">Bug, UX issue, or just a thought — we read everything.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-white/[0.05] hover:bg-white/[0.10] flex items-center justify-center text-white/40 hover:text-white transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {done ? (
          <div className="py-10 text-center text-sm text-emerald-400">
            <div className="text-3xl mb-2">✓</div>
            Thanks — we got it.
          </div>
        ) : (
          <>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 2000))}
              placeholder="What's on your mind?"
              rows={5}
              maxLength={2000}
              autoFocus
              className="w-full text-sm bg-white/[0.04] border border-white/[0.08] rounded-xl px-3.5 py-3 text-white placeholder-white/30 outline-none focus:border-white/20 transition-colors resize-none"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-[11px] text-white/30">{comment.length}/2000</span>
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending || !comment.trim()}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-scout-blue text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
